import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../../../theme/useTheme';
import DLOAddToFavoritesModal from '../DLOAddToFavoritesModal';
import DLOLoader from '../DLOLoader';
import DLOTooltip from '../DLOTooltip';
import { createApiClient, apiClient } from '../../../../api/apiClient';
import { fetchDashboardSchedule } from '../../../../store/thunks/dloThunks';
import { useDispatch } from 'react-redux';
import { formatNumberWithSeparators, formatCasesValue } from '../../utils/numberFormatUtils';
import useSyncedHorizontalScrollbar from '../../../../hooks/useSyncedHorizontalScrollbar.ts';

// Icons from the Figma design
const RadioButtonIcon = ({ checked = false, color = '#3A3A3A' }) => {
  // SVG fill attributes are not covered by the global dark-mode CSS sweep, so
  // pick a visible ring/dot colour explicitly: light grey when unchecked, brand
  // blue when checked.
  const { resolvedTheme } = useTheme();
  const fill = resolvedTheme === 'dark' ? (checked ? '#3D8BFF' : '#B0B0B0') : color;
  return (
    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58171 12.4183 0 8 0C3.58171 0 0 3.58171 0 8C0 12.4183 3.58171 16 8 16ZM8 14.6667C11.682 14.6667 14.6667 11.682 14.6667 8C14.6667 4.31803 11.682 1.33333 8 1.33333C4.31803 1.33333 1.33333 4.31803 1.33333 8C1.33333 11.682 4.31803 14.6667 8 14.6667Z"
        fill={fill}
      />
      {checked && (
        <Path
          d="M8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12Z"
          fill={fill}
        />
      )}
    </Svg>
  );
};

const StarIcon = ({ filled = false, favorite = false }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path 
      d="M14.81 8.62L22 9.24L16.55 13.97L18.18 21L12 17.27L5.82 21L7.46 13.97L2 9.24L9.19 8.63L12 2L14.81 8.62Z" 
      fill={favorite ? '#FFBF1C' : filled ? '#C0C0C0' : '#C0C0C0'} 
    />
  </Svg>
);

const UnfoldMoreIcon = ({ color = '#3A3A3A' }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path 
      d="M8 3.88667L10.1133 6L11.0533 5.06L8 2L4.94 5.06L5.88667 6L8 3.88667ZM8 12.1133L5.88667 10L4.94667 10.94L8 14L11.06 10.94L10.1133 10L8 12.1133Z" 
      fill={color} 
    />
  </Svg>
);

// Data interfaces
export interface ScheduleRowData {
  id: string;
  period: string;
  week: string;
  year: string;
  forecastedCases: string;
  actualCases: string;
  actualCasesColor?: 'error' | 'success' | 'default';
  createdBy: string;
  modifiedBy: string;
  createdByEmail?: string; // Store the original email for API calls
  modifiedByEmail?: string; // Store the original email for API calls
  recommendedHours: string;
  scheduledHours: string;
  estimatedHours?: string;
  actualHours?: string;
  isSelected?: boolean;
  isFavorite?: boolean;
  fav_name?: string; // Favorite name for display in dropdowns
  // Additional fields needed for API calls
  periodNumber?: number; // Numeric period for API
  weekNumber?: number;   // Numeric week for API
  yearNumber?: number;   // Numeric year for API
  locId?: number;        // Location ID for API
  status?: string; // Backend status
  uiStatus?: string; // UI status for frontend
  forecastedKcph?: string; 
  scheduledKcph?: string; 
  estimatedKcph?: string; 
  actualKcph?: string; 
  targetKcph?: string; 
  headcount?: string;
  // New breakdown fields for tooltips
  directEstimated?: string;
  directForecasted?: string;
  directActual?: string;
  directScheduled?: string;
  nonDirectEstimated?: string;
  nonDirectForecasted?: string;
  nonDirectActual?: string;
  nonDirectScheduled?: string;
  otherEstimated?: string;
  otherForecasted?: string;
  otherActual?: string;
  otherScheduled?: string;
  differenceofHours?: number; // Computed: recommendedHours - scheduledHours
}

// Column header data - All columns are sortable with icons
const columnHeaders = [
  { key: 'fav', label: 'Favorite Name', sortable: true },
  { key: 'period', label: 'PERIOD', sortable: true },
  { key: 'week', label: 'WEEK', sortable: true },
  { key: 'year', label: 'YEAR', sortable: true },
  { key: 'forecastedCases', label: 'FORECASTED CASES', sortable: true },
  { key: 'actualCases', label: 'ACTUAL CASES', sortable: true },
  { key: 'createdBy', label: 'CREATED BY', sortable: true },
  { key: 'modifiedBy', label: 'MODIFIED BY', sortable: true },
  { key: 'recommendedHours', label: 'FORECASTED HOURS', sortable: true }, // This should have sort icon
  { key: 'scheduledHours', label: 'SCHEDULED HOURS', sortable: true },
  { key:'differenceofHours', label:'Difference of Hours', sortable:true},
  { key: 'estimatedHours', label: 'ESTIMATED HOURS', sortable: true },
  { key: 'actualHours', label: 'ACTUAL HOURS', sortable: true },
  { key:'forecastedKcph', label:'Forecasted KCPH', sortable:true},
  { key:'scheduledKcph', label:'Scheduled KCPH', sortable:true},
  { key:'estimatedKcph', label:'Estimated KCPH', sortable:true},
  { key:'actualKcph', label:'Actual KCPH', sortable:true},
  { key:'targetKcph', label:'Target KCPH', sortable:true},
  { key:'headcount', label:'Headcount', sortable:true},
  { key: 'uiStatus', label: 'STATUS', sortable: true },
];

// Sample data from Figma design
const sampleData: ScheduleRowData[] = [
  {
    id: '1',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '10,000 (-20%)',
    actualCasesColor: 'error',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
    // Sample breakdown fields for tooltips
    directEstimated: '120.5',
    directForecasted: '150.0',
    directActual: '125.2',
    directScheduled: '140.0',
    nonDirectEstimated: '80.3',
    nonDirectForecasted: '70.0',
    nonDirectActual: '85.1',
    nonDirectScheduled: '75.0',
    otherEstimated: '39.2',
    otherForecasted: '20.0',
    otherActual: '30.7',
    otherScheduled: '20.0',
  },
  {
    id: '2',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '11,800',
    actualCases: '12,000 (-4.3%)',
    actualCasesColor: 'success',
    createdBy: 'John Doe',
    modifiedBy: 'Jane Doe',
    recommendedHours: '220',
    scheduledHours: '222',
    estimatedHours: '218',
    isSelected: false,
    isFavorite: true, // This row has a favorite star
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
    // Sample breakdown fields for tooltips
    directEstimated: '110.0',
    directForecasted: '130.0',
    directActual: '115.5',
    directScheduled: '132.0',
    nonDirectEstimated: '75.0',
    nonDirectForecasted: '65.0',
    nonDirectActual: '80.2',
    nonDirectScheduled: '70.0',
    otherEstimated: '35.0',
    otherForecasted: '25.0',
    otherActual: '26.3',
    otherScheduled: '20.0',
  },
  {
    id: '3',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '10,000 (-20%)',
    actualCasesColor: 'error',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '4',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '--',
    actualCasesColor: 'default',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '5',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '--',
    actualCasesColor: 'default',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    scheduledHours: '235',
    estimatedHours: '230',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '6',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '--',
    actualCasesColor: 'default',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '7',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '--',
    actualCasesColor: 'default',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '8',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '10,000 (-20%)',
    actualCasesColor: 'error',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '9',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '11,800',
    actualCases: '12,000 (-4.3%)',
    actualCasesColor: 'success',
    createdBy: 'John Doe',
    modifiedBy: 'Jane Doe',
    recommendedHours: '220',
    estimatedHours: '218',
    scheduledHours: '222',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
  {
    id: '10',
    period: 'Content',
    week: '16',
    year: '2025',
    forecastedCases: '12,500',
    actualCases: '--',
    actualCasesColor: 'default',
    createdBy: 'Jane Doe',
    modifiedBy: 'John Doe',
    recommendedHours: '240',
    estimatedHours: '230',
    scheduledHours: '235',
    isSelected: false,
    isFavorite: false,
    // API fields
    periodNumber: 10,
    weekNumber: 16,
    yearNumber: 2025,
    locId: 1016,
  },
];

// Column Header Component
interface ColumnHeaderProps {
  label: string;
  sortable: boolean;
  onSort?: () => void;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({ label, sortable, onSort }) => (
  <TouchableOpacity 
    style={styles.headerCell} 
    onPress={sortable ? onSort : undefined}
    activeOpacity={sortable ? 0.7 : 1}
  >
    <View style={styles.headerContent}>
      <Text style={styles.headerText}>{label}</Text>
      {sortable && <UnfoldMoreIcon />}
    </View>
    <View style={styles.resizeHandle} />
  </TouchableOpacity>
);

// Data Cell Component
interface DataCellProps {
  value: string;
  textColor?: 'error' | 'success' | 'default';
  flex?: number;
}

const DataCell: React.FC<DataCellProps & { tooltip?: React.ReactNode; tooltipPosition?: import('../DLOTooltip').DLOTooltipPosition }> = ({ value, textColor = 'default', flex = 1, tooltip, tooltipPosition = 'top' }) => (
  <View style={[styles.dataCell, { flex }]}> 
    {tooltip ? (
      <DLOTooltip
        content={typeof tooltip === 'string' ? tooltip : String(tooltip)}
        position={tooltipPosition}
        trigger="hover"
        maxWidth={260}
        verticalOffset={-14}
      >
        <Text style={[
          styles.cellText,
          textColor === 'error' && styles.errorText,
          textColor === 'success' && styles.successText,
        ]}>
          {value}
        </Text>
      </DLOTooltip>
    ) : (
      <Text style={[
        styles.cellText,
        textColor === 'error' && styles.errorText,
        textColor === 'success' && styles.successText,
      ]}>
        {value}
      </Text>
    )}
  </View>
);

// Main Component Props
export interface DLOScheduleTableContentProps {
  data?: ScheduleRowData[];
  loading?: boolean;
  onRowSelect?: (id: string, selected: boolean) => void;
  onRowFavorite?: (id: string, favorite: boolean) => void;
  onHeaderSort?: (column: string) => void;
  style?: ViewStyle;
}

// Helper function to generate tooltip content for hours fields
const generateHoursTooltip = (row: ScheduleRowData, type: 'estimated' | 'forecasted' | 'actual' | 'scheduled'): string => {
  let directValue = '--';
  let nonDirectValue = '--';
  let otherValue = '--';

  switch (type) {
    case 'estimated':
      directValue = row.directEstimated || '--';
      nonDirectValue = row.nonDirectEstimated || '--';
      otherValue = row.otherEstimated || '--';
      break;
    case 'forecasted':
      directValue = row.directForecasted || '--';
      nonDirectValue = row.nonDirectForecasted || '--';
      otherValue = row.otherForecasted || '--';
      break;
    case 'actual':
      directValue = row.directActual || '--';
      nonDirectValue = row.nonDirectActual || '--';
      otherValue = row.otherActual || '--';
      break;
    case 'scheduled':
      directValue = row.directScheduled || '--';
      nonDirectValue = row.nonDirectScheduled || '--';
      otherValue = row.otherScheduled || '--';
      break;
  }

  return `Labor Categories\nDirect: ${directValue}\nIndirect: ${nonDirectValue}\nOther: ${otherValue}`;
};

const DLOScheduleTableContent: React.FC<DLOScheduleTableContentProps> = ({
  data = sampleData,
  loading = false,
  onRowSelect,
  onRowFavorite,
  onHeaderSort,
  style,
}) => {
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [favoriteRows, setFavoriteRows] = useState<Set<string>>(new Set());
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [pendingFavoriteRowId, setPendingFavoriteRowId] = useState<string | null>(null);
  const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
  const [sortByFavorite, setSortByFavorite] = useState(false);
  const { tableScrollRef, topScrollBarRef, contentWidth, handleContentWidthChange } =
    useSyncedHorizontalScrollbar(1600);
  
  const screenWidth = Dimensions.get('window').width;
  const isSmallScreen = screenWidth < 768;
  const dispatch = useDispatch();

  // Initialize favorite rows based on data when component mounts or data changes
  useEffect(() => {
    const initialFavorites = new Set<string>();
    data.forEach(row => {
      if (row.isFavorite) {
        initialFavorites.add(row.id);
      }
    });
    setFavoriteRows(initialFavorites);
    console.log('Initialized favorites from data:', Array.from(initialFavorites));
    
    // Reset sortByFavorite if no favorites exist
    if (initialFavorites.size === 0) {
      setSortByFavorite(false);
    }
  }, [data]);
  
  // Check if there are any favorite schedules
  const hasFavorites = favoriteRows.size > 0;

  // API function to add a favorite
  const addToFavorites = async (row: ScheduleRowData, favName: string) => {
    try {
      console.log('the row is', row)
      const api = createApiClient();
      const payload = {
        period: row.periodNumber || parseInt(row.period, 10),
        week: ((row?.periodNumber ?? parseInt(row.period, 10)) - 1) * 4 + (row.weekNumber || parseInt(row.week, 10)),
        year: row.yearNumber || parseInt(row.year, 10),
        loc_id: row.locId || 1016,
        favName: favName,
      };

      console.log('Adding to favorites with payload:', payload);
      
      await apiClient(api, '/dlo/dashboard/favorites/add', 'PUT', payload).then(() => {
        dispatch(fetchDashboardSchedule({}) as any);
      });
      
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error;
    }
  };

  // API function to remove from favorites
  const removeFromFavorites = async (row: ScheduleRowData) => {
    try {
      const api = createApiClient();
      const payload = {
        period: row.periodNumber || parseInt(row.period, 10),
        week: ((row?.periodNumber ?? parseInt(row.period, 10)) - 1) * 4 + (row.weekNumber || parseInt(row.week, 10)),
        year: row.yearNumber || parseInt(row.year, 10),
        loc_id: row.locId || 1016,
        // Note: No favName property for removal
      };

      console.log('Removing from favorites with payload:', payload);
      
      // Using the correct remove endpoint
      await apiClient(api, '/dlo/dashboard/favorites/remove', 'PUT', payload).then(() => {
        dispatch(fetchDashboardSchedule({}) as any);
      });

      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      throw error;
    }
  };

  const handleRowSelect = (id: string) => {
    const isSelected = selectedRow === id;
    setSelectedRow(isSelected ? null : id);
    onRowSelect?.(id, !isSelected);
  };

  const handleRowFavorite = async (id: string) => {
    const row = data.find(r => r.id === id);
    if (!row) return;

    const isCurrentlyFavorite = favoriteRows.has(id);
    
    if (isCurrentlyFavorite) {
      // If already favorite, remove it directly with API call
      setIsLoadingFavorite(true);
      try {
        await removeFromFavorites(row);
        
        // Only update state after successful API call
        const newFavorites = new Set(favoriteRows);
        newFavorites.delete(id);
        setFavoriteRows(newFavorites);
        onRowFavorite?.(id, false);
        
        console.log(`Successfully removed row ${id} from favorites`);
      } catch (error) {
        console.error(`Failed to remove row ${id} from favorites:`, error);
        // TODO: Show error message to user
      } finally {
        setIsLoadingFavorite(false);
      }
    } else {
      // If not favorite, show the modal to add it
      setPendingFavoriteRowId(id);
      setShowFavoriteModal(true);
    }
  };

  const handleSaveFavorite = async (title: string) => {
    if (pendingFavoriteRowId) {
      const row = data.find(r => r.id === pendingFavoriteRowId);
      if (!row) {
        console.error(`Row with ID ${pendingFavoriteRowId} not found`);
        handleCloseFavoriteModal();
        return;
      }

      setIsLoadingFavorite(true);
      try {
        await addToFavorites(row, title);
        
        // Only update state after successful API call
        const newFavorites = new Set(favoriteRows);
        newFavorites.add(pendingFavoriteRowId);
        setFavoriteRows(newFavorites);
        onRowFavorite?.(pendingFavoriteRowId, true);
        
        console.log(`Successfully added row ${pendingFavoriteRowId} to favorites with title: "${title}"`);
      } catch (error) {
        console.error(`Failed to add row ${pendingFavoriteRowId} to favorites:`, error);
        // TODO: Show error message to user
        // For now, we'll close the modal without updating state
      } finally {
        setIsLoadingFavorite(false);
        // Close modal and reset state
        setShowFavoriteModal(false);
        setPendingFavoriteRowId(null);
      }
    }
  };

  const handleCloseFavoriteModal = () => {
    setShowFavoriteModal(false);
    setPendingFavoriteRowId(null);
  };


  // Sort data by favorite if sortByFavorite is true
  const sortedData = sortByFavorite
    ? [...data].sort((a, b) => {
        // true first, false last
        if ((a.isFavorite ? 1 : 0) === (b.isFavorite ? 1 : 0)) return 0;
        return b.isFavorite ? 1 : -1;
      })
    : data;

  // Custom onHeaderSort handler for star icon
  const handleHeaderSort = (column: string) => {
    if (column === 'favorite') {
      // Only allow toggling sort if there are favorites
      if (hasFavorites) {
        setSortByFavorite((prev) => !prev);
      }
    } else {
      onHeaderSort?.(column);
    }
  };

  return (
    <View style={[styles.container, style]}>
      {Platform.OS === 'web' &&
        React.createElement(
          'div',
          {
            ref: topScrollBarRef,
            style: { overflowX: 'scroll', overflowY: 'hidden', height: 14 },
          },
          React.createElement('div', {
            style: { width: Math.max(contentWidth, 1), height: 1 },
          })
        )}
      <ScrollView 
        ref={tableScrollRef}
        horizontal 
        showsHorizontalScrollIndicator={true}
        onContentSizeChange={(width) => handleContentWidthChange(width)}
      >
        <View style={styles.tableContainer}>
          {/* Table Header */}
          <View style={styles.headerRow}>
            {/* Select All Checkbox */}
            {/* <TouchableOpacity style={styles.checkboxCell} onPress={handleSelectAll}>
              <RadioButtonIcon checked={allSelected} />
            </TouchableOpacity> */}
            
            {/* Star Header */}
            <TouchableOpacity
              style={[styles.starCell, !hasFavorites && styles.disabledCell]}
              onPress={() => handleHeaderSort('favorite')}
              activeOpacity={hasFavorites ? 0.7 : 1}
              disabled={!hasFavorites}
            >
              <DLOTooltip
                    content={hasFavorites ? 'Pin favorite schedules to top' : 'No favorite schedules to pin'}
                    position="right"
                    trigger="hover"
                    maxWidth={200}
                  >
                    <StarIcon filled favorite={sortByFavorite} />
                  </DLOTooltip>

             
            </TouchableOpacity>

            {/* Column Headers */}
            {columnHeaders.map((header) => (
              <ColumnHeader
                key={header.key}
                label={header.label}
                sortable={header.sortable}
                onSort={() => onHeaderSort?.(header.key)}
              />
            ))}

            {/* Action Buttons Header */}
            <View style={styles.actionsCell}>
              {/* Placeholder for action buttons */}
            </View>
          </View>

          {/* Table Rows */}
          {sortedData.map((row) => (
            <View key={row.id} style={styles.dataRow}>
              {/* Row Selection Radio Button (single select) */}
              <TouchableOpacity 
                style={styles.checkboxCell} 
                onPress={() => handleRowSelect(row.id)}
              >
                <RadioButtonIcon checked={selectedRow === row.id} />
              </TouchableOpacity>

              {/* Favorite Star */}
              <TouchableOpacity 
                style={styles.starCell} 
                onPress={() => handleRowFavorite(row.id)}
              >
                {row.fav_name && favoriteRows.has(row.id) ? (
                  <DLOTooltip
                    content={row.fav_name}
                    position="right"
                    trigger="hover"
                    maxWidth={200}
                  >
                    <StarIcon 
                      filled 
                      favorite={favoriteRows.has(row.id)} 
                    />
                  </DLOTooltip>
                ) : (
                  <StarIcon 
                    filled 
                    favorite={favoriteRows.has(row.id)} 
                  />
                )}
              </TouchableOpacity>

              {/* Data Cells */}
               <DataCell value={row.fav_name || ''} />
              <DataCell value={row.period} />
              <DataCell value={row.week} />
              <DataCell value={row.year} />
              <DataCell value={formatCasesValue(row.forecastedCases)} />
              <DataCell 
                value={formatCasesValue(row.actualCases)} 
                textColor={row.actualCasesColor} 
              />
              <DataCell value={row.createdBy} />
              <DataCell value={row.modifiedBy} />
              <DataCell 
                value={row.recommendedHours}
                tooltip={generateHoursTooltip(row, 'forecasted')}
                tooltipPosition="right"
              />
              <DataCell 
                value={row.scheduledHours}
                tooltip={generateHoursTooltip(row, 'scheduled')}
                tooltipPosition="right"
              />
              <DataCell value={
                (() => {
                  const diff = (Number(row.recommendedHours) || 0) - (Number(row.scheduledHours) || 0);
                  const rounded = diff >= 0
                    ? Math.ceil(diff * 10) / 10
                    : Math.floor(diff * 10) / 10;
                  return rounded.toFixed(1);
                })()
              } />
              <DataCell 
                value={row.estimatedHours ?? ''}
                tooltip={generateHoursTooltip(row, 'estimated')}
                tooltipPosition="right"
              />
              <DataCell 
                value={row.actualHours ?? ''}
                tooltip={generateHoursTooltip(row, 'actual')}
                tooltipPosition="right"
              />
              <DataCell value={formatNumberWithSeparators(row.forecastedKcph)}/>
              <DataCell value={formatNumberWithSeparators(row.scheduledKcph)} />
              <DataCell value={formatNumberWithSeparators(row.estimatedKcph)} />
              <DataCell value={formatNumberWithSeparators(row.actualKcph)} />
              <DataCell value={formatNumberWithSeparators(row.targetKcph)} />
              <DataCell value={formatNumberWithSeparators(row.headcount)} />
              <DataCell value={row.uiStatus || ''} />

              {/* Action Buttons */}
              <View style={styles.actionsCell}>
                {/* Placeholder for action buttons */}
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
      
      {/* Loading Overlay */}
      {loading && <DLOLoader />}
      
      {/* Add to Favorites Modal */}
      <DLOAddToFavoritesModal
        visible={showFavoriteModal}
        onClose={handleCloseFavoriteModal}
        onSave={handleSaveFavorite}
        isLoading={isLoadingFavorite}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FBFCFE',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    position: 'relative' as any,
  },
  tableContainer: {
    flexDirection: 'column',
    minWidth: 1600, // Further increased width to accommodate longer headers
  },
  headerRow: {
    flexDirection: 'row',
    minHeight: 48,
    borderTopWidth: 2,
    borderTopColor: '#D7E5F8',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
    alignItems: 'center',
  },
  dataRow: {
    flexDirection: 'row',
    minHeight: 48,
    paddingVertical: 8,
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
    alignItems: 'center',
  },
  checkboxCell: {
    paddingHorizontal: 12,
    paddingLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    width: 40,
  },
  starCell: {
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledCell: {
    opacity: 0.4,
  },
  headerCell: {
    minHeight: 48,
    paddingHorizontal: 8,
    paddingVertical: 12,
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    minWidth: 150,
    flex: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'stretch',
    flexWrap: 'nowrap',
  },
  headerText: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  resizeHandle: {
    position: 'absolute',
    right: 0,
    top: 14,
    width: 2,
    height: 20,
    borderRadius: 2,
    backgroundColor: '#D7E5F8',
  },
  dataCell: {
    paddingHorizontal: 8,
    justifyContent: 'center',
    alignItems: 'flex-start',
    minWidth: 150,
    flex: 1,
  },
  cellText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    alignSelf: 'stretch',
  },
  errorText: {
    color: '#D40020',
  },
  successText: {
    color: '#008553',
  },
  actionsCell: {
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexDirection: 'row',
  },
});

export default DLOScheduleTableContent;
